import React, { CSSProperties } from 'react';
import { Zap, Crown, Sparkles, Star, ArrowRight } from 'lucide-react';

interface PlanDemoProps {
  planType: 'free' | 'basic' | 'standard' | 'pro';
}

const planConfig = {
  free: {
    icon: Zap,
    label: 'Free',
    featureText: '3 parallel messages',
    showUpgrade: true,
    upgradeText: 'Go Pro',
    colorVar: '--subscription-plan-free-text',
    bgVar: '--subscription-plan-free-bg',
    borderVar: '--subscription-plan-free-border',
    iconVar: '--subscription-plan-free-icon',
    featureTextVar: undefined,
  },
  basic: {
    icon: Sparkles,
    label: 'Basic',
    featureText: 'Unlimited messages',
    showUpgrade: true,
    upgradeText: 'Upgrade',
    colorVar: '--subscription-plan-basic-text',
    bgVar: '--subscription-plan-basic-bg',
    borderVar: '--subscription-plan-basic-border',
    iconVar: '--subscription-plan-basic-icon',
    featureTextVar: undefined,
  },
  standard: {
    icon: Star,
    label: 'Standard',
    featureText: 'Unlimited + Advanced',
    showUpgrade: true,
    upgradeText: 'Upgrade',
    colorVar: '--subscription-plan-standard-text',
    bgVar: '--subscription-plan-standard-bg',
    borderVar: '--subscription-plan-standard-border',
    iconVar: '--subscription-plan-standard-icon',
    featureTextVar: undefined,
  },
  pro: {
    icon: Crown,
    label: 'Pro',
    featureText: 'Unlimited + Offline + Premium',
    showUpgrade: false,
    upgradeText: '',
    colorVar: '--subscription-plan-pro-text',
    bgVar: '--subscription-plan-pro-bg',
    borderVar: '--subscription-plan-pro-border',
    iconVar: '--subscription-plan-pro-icon',
    featureTextVar: '--subscription-plan-pro-feature-text',
  },
};

export const SubscriptionPlanDemo: React.FC<PlanDemoProps> = ({ planType }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  
  const config = planConfig[planType];
  const IconComponent = config.icon;

  const containerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderRadius: '8px',
    border: `1px solid var(${config.borderVar})`,
    background: `var(${config.bgVar})`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 1px 3px var(--subscription-plan-button-shadow), 0 1px 2px var(--subscription-plan-button-shadow)',
    minHeight: '44px',
    width: 'fit-content',
    minWidth: 'auto',
  };

  const leftSectionStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: '1',
    minWidth: '0',
    marginRight: '12px',
  };

  const iconStyle: CSSProperties = {
    width: '16px',
    height: '16px',
    color: `var(${config.iconVar})`,
    flexShrink: 0,
  };

  const planInfoStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: '0',
    flex: '1',
  };

  const planLabelStyle: CSSProperties = {
    fontSize: '14px',
    fontWeight: '600',
    color: `var(${config.colorVar})`,
    lineHeight: '1.1',
    whiteSpace: 'nowrap',
  };

  const featureTextStyle: CSSProperties = {
    fontSize: '11px',
    color: config.featureTextVar ? `var(${config.featureTextVar})` : `var(${config.colorVar})`,
    opacity: config.featureTextVar ? '1' : '0.8',
    fontWeight: '500',
    lineHeight: '1.2',
    whiteSpace: 'nowrap',
  };

  const getButtonStyle = (): CSSProperties => {
    const baseStyle: CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      borderRadius: '6px',
      color: 'var(--color-text-inverse)',
      fontSize: '12px',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
      whiteSpace: 'nowrap',
      minWidth: 'auto',
      justifyContent: 'center',
      flexShrink: 0,
      marginLeft: 'auto',
    };

    // Special attractive styling for Free plan "Go Pro" button
    if (planType === 'free') {
      baseStyle.background = 'var(--subscription-plan-free-button)';
      baseStyle.boxShadow = isHovered 
        ? '0 4px 12px rgba(59, 130, 246, 0.4), 0 2px 6px rgba(59, 130, 246, 0.3)'
        : '0 2px 8px rgba(59, 130, 246, 0.3), 0 1px 3px rgba(59, 130, 246, 0.2)';
      
      if (isHovered) {
        baseStyle.background = 'var(--subscription-plan-free-button-hover)';
      }
    } else {
      // Other plans use their respective colors
      baseStyle.background = `var(--subscription-plan-${planType}-button)`;
      baseStyle.boxShadow = isHovered 
        ? `0 4px 12px var(--subscription-plan-button-shadow), 0 2px 6px var(--subscription-plan-button-shadow)`
        : `0 2px 8px var(--subscription-plan-button-shadow), 0 1px 3px var(--subscription-plan-button-shadow)`;
    }

    return baseStyle;
  };

  const handleUpgrade = () => {
    console.log(`Upgrade clicked for ${planType} plan`);
  };

  return (
    <div style={containerStyle}>
      <div style={leftSectionStyle}>
        <IconComponent style={iconStyle} />
        <div style={planInfoStyle}>
          <div style={planLabelStyle}>{config.label} Plan</div>
          <div style={featureTextStyle}>{config.featureText}</div>
        </div>
      </div>

      {config.showUpgrade && (
        <button
          style={getButtonStyle()}
          onClick={handleUpgrade}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          data-testid="upgrade-button"
        >
          <span>{config.upgradeText}</span>
          <ArrowRight style={{ width: '14px', height: '14px' }} />
        </button>
      )}
    </div>
  );
};

export const AllPlansDemo: React.FC = () => {
  const plans: Array<'free' | 'basic' | 'standard' | 'pro'> = ['free', 'basic', 'standard', 'pro'];
  
  return (
    <div style={{ 
      padding: '40px 20px', 
      backgroundColor: 'var(--color-bg)',
      color: 'var(--color-text)',
      minHeight: '100vh',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700', 
            marginBottom: '8px',
            color: 'var(--color-text-strong)',
            letterSpacing: '-0.025em'
          }}>
            Subscription Plans
          </h1>
          <p style={{
            fontSize: '16px',
            color: 'var(--color-text-muted)',
            margin: '0',
            fontWeight: '400'
          }}>
            Choose the perfect plan for your messaging needs
          </p>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gap: '20px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))'
        }}>
          {plans.map((plan, index) => (
            <div key={plan} style={{
              position: 'relative',
              transform: `translateY(${index * 2}px)`,
              transition: 'transform 0.3s ease'
            }}>
              <SubscriptionPlanDemo planType={plan} />
            </div>
          ))}
        </div>
        
        <div style={{
          marginTop: '40px',
          padding: '20px',
          backgroundColor: 'var(--color-surface-muted)',
          borderRadius: '12px',
          border: '1px solid var(--color-border)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--color-text-strong)',
            margin: '0 0 8px 0'
          }}>
            💡 Design Features
          </h3>
          <ul style={{
            fontSize: '14px',
            color: 'var(--color-text-muted)',
            margin: '0',
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>Clean, modern design with subtle gradients</li>
            <li>Consistent spacing and typography hierarchy</li>
            <li>Smooth hover animations and micro-interactions</li>
            <li>Professional color scheme for each plan tier</li>
            <li>No nested rectangles - clean flat design</li>
            <li><strong>Attractive "Go Pro" button with blue gradient and enhanced shadows</strong></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AllPlansDemo;